/**
 * Small inline SVG icon set for the portal (no icon font).
 * @param {string} name
 * @param {{ size?: number, className?: string }} [opts]
 */
export function icon(name, { size = 18, className = "" } = {}) {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("width", String(size));
  svg.setAttribute("height", String(size));
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "none");
  svg.setAttribute("aria-hidden", "true");
  if (className) svg.setAttribute("class", className);

  const stroke = {
    stroke: "currentColor",
    "stroke-width": "1.75",
    "stroke-linecap": "round",
    "stroke-linejoin": "round",
  };

  /** @type {Record<string, Array<[string, Record<string, string>]>>} */
  const paths = {
    wallet: [
      ["rect", { x: "2", y: "6", width: "20", height: "14", rx: "2.5", ...stroke }],
      ["path", { d: "M2 10h20", ...stroke }],
      ["circle", { cx: "17", cy: "14.5", r: "1.25", fill: "currentColor", stroke: "none" }],
    ],
    settings: [
      [
        "path",
        {
          d: "M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z",
          ...stroke,
        },
      ],
      [
        "path",
        {
          d: "M19.4 15a1.7 1.7 0 0 0 .34 1.86l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .6 1.7 1.7 0 0 0-.4 1.1V21a2 2 0 1 1-4 0v-.09a1.7 1.7 0 0 0-1.1-1.56 1.7 1.7 0 0 0-1.86.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-.6-1 1.7 1.7 0 0 0-1.1-.4H3a2 2 0 1 1 0-4h.09a1.7 1.7 0 0 0 1.56-1.1 1.7 1.7 0 0 0-.34-1.86l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-.6 1.7 1.7 0 0 0 .4-1.1V3a2 2 0 1 1 4 0v.09a1.7 1.7 0 0 0 .4 1.1 1.7 1.7 0 0 0 1 .6 1.7 1.7 0 0 0 1.86-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.7 1.7 0 0 0 19.4 9c.26.4.4.86.4 1.34v.1c0 .48-.14.94-.4 1.34-.2.3-.3.66-.3 1.02s.1.72.3 1.02Z",
          ...stroke,
        },
      ],
    ],
    copy: [
      ["rect", { x: "9", y: "9", width: "13", height: "13", rx: "2", ...stroke }],
      ["path", { d: "M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1", ...stroke }],
    ],
    external: [
      ["path", { d: "M14 3h7v7", ...stroke }],
      ["path", { d: "M10 14 21 3", ...stroke }],
      ["path", { d: "M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5", ...stroke }],
    ],
    refresh: [
      ["path", { d: "M21 12a9 9 0 1 1-2.6-6.4", ...stroke }],
      ["path", { d: "M21 3v6h-6", ...stroke }],
    ],
    chevron: [["path", { d: "m9 18 6-6-6-6", ...stroke }]],
    check: [["path", { d: "m5 12 5 5L20 7", ...stroke }]],
    alert: [
      ["path", { d: "M12 9v4", ...stroke }],
      ["path", { d: "M12 17h.01", ...stroke }],
      ["path", { d: "M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z", ...stroke }],
    ],
    deposit: [
      ["path", { d: "M12 3v12", ...stroke }],
      ["path", { d: "m7 10 5 5 5-5", ...stroke }],
      ["path", { d: "M5 21h14", ...stroke }],
    ],
    withdraw: [
      ["path", { d: "M12 21V9", ...stroke }],
      ["path", { d: "m7 14 5-5 5 5", ...stroke }],
      ["path", { d: "M5 3h14", ...stroke }],
    ],
    feedback: [
      ["path", { d: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z", ...stroke }],
    ],
    /** Rotated square - block / tip glyph (handoff status pill). */
    block: [
      [
        "rect",
        {
          x: "7",
          y: "7",
          width: "10",
          height: "10",
          rx: "1.5",
          transform: "rotate(45 12 12)",
          fill: "currentColor",
          stroke: "none",
        },
      ],
    ],
    /** Half-filled circle - colorblind contrast toggle. */
    contrast: [
      ["circle", { cx: "12", cy: "12", r: "9", ...stroke }],
      ["path", { d: "M12 3a9 9 0 0 1 0 18z", fill: "currentColor", stroke: "none" }],
    ],
  };

  const defs = paths[name];
  if (!defs) return svg;
  for (const [tag, attrs] of defs) {
    const el = document.createElementNS("http://www.w3.org/2000/svg", tag);
    for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
    svg.append(el);
  }
  return svg;
}

/**
 * Icon + label button row helper for popover nav.
 * @param {{ label: string, iconName?: string, onClick: () => void, variant?: string }} opts
 */
export function iconNavButton({ label, iconName = "chevron", onClick, variant = "" }) {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = `icon-nav-btn${variant ? ` ${variant}` : ""}`;
  if (iconName) btn.append(icon(iconName, { size: 16 }));
  const span = document.createElement("span");
  span.textContent = label;
  btn.append(span);
  btn.addEventListener("click", onClick);
  return btn;
}
